import { useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { Copy, Download, Save, ChevronRight, Calendar, Users, Map, Sparkles, Target, Gift, Clock, Brain } from 'lucide-react'
import type { EventResult } from '@creanizer/types'
import { useEventStore } from '../../../../stores/eventStore'

interface EventResultProps {
  results: EventResult[]
  mode?: 'single' | 'sequence' | 'branch' | 'campaign'
}

export function EventResult({ results, mode = 'single' }: EventResultProps) {
  const [activeTab, setActiveTab] = useState('basic')
  const [selectedResult, setSelectedResult] = useState(0)
  const { exportSingleAsText, exportSingleAsJSON } = useEventStore()
  
  if (!results || results.length === 0) return null
  
  const result = results[selectedResult]
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }
  
  const formatEventSummary = () => {
    return `
【イベント: ${result.basicInfo.name}】
タイプ: ${result.basicInfo.type}
カテゴリ: ${result.basicInfo.category}
概要: ${result.basicInfo.summary}

■ 詳細説明
${result.basicInfo.description}

■ 発生条件
トリガー: ${result.basicInfo.trigger}
${result.basicInfo.prerequisites ? `前提条件: ${result.basicInfo.prerequisites.join(', ')}` : ''}
`
  }
  
  return (
    <div className="space-y-4">
      {mode !== 'single' && results.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {results.map((r, index) => (
            <Button
              key={index}
              variant={selectedResult === index ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedResult(index)}
            >
              {r.basicInfo.name}
            </Button>
          ))}
        </div>
      )}
      
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <CardTitle className="text-xl flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                {result.basicInfo.name}
              </CardTitle>
              <div className="flex flex-wrap gap-2">
                <Badge>{result.basicInfo.type}</Badge>
                <Badge variant="secondary">{result.basicInfo.category}</Badge>
                <Badge variant="outline">{result.basicInfo.nature}</Badge>
                {result.metadata?.difficulty && (
                  <Badge variant="destructive">難易度: {result.metadata.difficulty}/10</Badge>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => copyToClipboard(formatEventSummary())}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-4 lg:grid-cols-8 w-full">
              <TabsTrigger value="basic">基本</TabsTrigger>
              <TabsTrigger value="scenario">シナリオ</TabsTrigger>
              <TabsTrigger value="variations">バリエ</TabsTrigger>
              <TabsTrigger value="participants">参加者</TabsTrigger>
              <TabsTrigger value="choices">選択肢</TabsTrigger>
              <TabsTrigger value="consequences">結果</TabsTrigger>
              <TabsTrigger value="progression">進行</TabsTrigger>
              <TabsTrigger value="mechanics">メカニクス</TabsTrigger>
            </TabsList>
            
            <TabsContent value="basic" className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">概要</h3>
                <p className="text-sm">{result.basicInfo.summary}</p>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">詳細説明</h3>
                <p className="text-sm whitespace-pre-wrap">{result.basicInfo.description}</p>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">発生条件</h3>
                <div className="space-y-1">
                  <p className="text-sm"><strong>トリガー:</strong> {result.basicInfo.trigger}</p>
                  {result.basicInfo.prerequisites && result.basicInfo.prerequisites.length > 0 && (
                    <div>
                      <strong className="text-sm">前提条件:</strong>
                      <ul className="list-disc list-inside text-sm mt-1">
                        {result.basicInfo.prerequisites.map((prereq, i) => (
                          <li key={i}>{prereq}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              
              {result.basicInfo.timing && (
                <div>
                  <h3 className="font-semibold mb-2">タイミング</h3>
                  <div className="space-y-1 text-sm">
                    <p><strong>いつ:</strong> {result.basicInfo.timing.when}</p>
                    <p><strong>継続時間:</strong> {result.basicInfo.timing.duration}</p>
                    <p><strong>緊急度:</strong> {result.basicInfo.timing.urgency}</p>
                    {result.basicInfo.timing.deadline && (
                      <p><strong>期限:</strong> {result.basicInfo.timing.deadline}</p>
                    )}
                  </div>
                </div>
              )}
              
              {result.basicInfo.scale && (
                <div>
                  <h3 className="font-semibold mb-2">規模</h3>
                  <div className="space-y-1 text-sm">
                    <p><strong>影響範囲:</strong> {result.basicInfo.scale.scope}</p>
                    <p><strong>重要度:</strong> {result.basicInfo.scale.importance}</p>
                    <p><strong>可視性:</strong> {result.basicInfo.scale.visibility}</p>
                  </div>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="scenario" className="space-y-4">
              {result.scenario?.opening && (
                <div>
                  <h3 className="font-semibold mb-2">オープニング</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>シーン:</strong> {result.scenario.opening.scene}</p>
                    <p><strong>フック:</strong> {result.scenario.opening.hook}</p>
                    <p><strong>雰囲気:</strong> {result.scenario.opening.atmosphere}</p>
                    <p><strong>最初の情報:</strong> {result.scenario.opening.initialReveal}</p>
                  </div>
                </div>
              )}
              
              {result.scenario?.development && (
                <div>
                  <h3 className="font-semibold mb-2">展開</h3>
                  {result.scenario.development.mainEvents && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium">メインイベント</h4>
                      {result.scenario.development.mainEvents.map((event, i) => (
                        <Card key={i}>
                          <CardContent className="pt-4">
                            <Badge className="mb-2">{event.order}. {event.timing}</Badge>
                            <p className="text-sm mb-2">{event.description}</p>
                            {event.requirement && (
                              <p className="text-xs text-muted-foreground">発生条件: {event.requirement}</p>
                            )}
                            <p className="text-xs text-muted-foreground">影響: {event.impact}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {result.scenario?.climax && (
                <div>
                  <h3 className="font-semibold mb-2">クライマックス</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>説明:</strong> {result.scenario.climax.description}</p>
                    <p><strong>緊張のピーク:</strong> {result.scenario.climax.tensionPoint}</p>
                    <p><strong>転換点:</strong> {result.scenario.climax.turningPoint}</p>
                    {result.scenario.climax.revelation && (
                      <p><strong>明かされる真実:</strong> {result.scenario.climax.revelation}</p>
                    )}
                    <p><strong>最終的な挑戦:</strong> {result.scenario.climax.challenge}</p>
                  </div>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="variations" className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-primary" />
                <h3 className="font-semibold">バリエーション ({result.scenario?.variations?.length || 0}種)</h3>
              </div>
              
              {result.scenario?.variations && result.scenario.variations.length > 0 ? (
                <div className="space-y-4">
                  {result.scenario.variations.map((variation, i) => (
                    <Card key={i}>
                      <CardHeader>
                        <CardTitle className="text-base">
                          {variation.name}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <p className="text-sm">{variation.description}</p>
                        <div>
                          <p className="text-sm font-medium">基本版との違い:</p>
                          <p className="text-sm text-muted-foreground">{variation.difference}</p>
                        </div>
                        {variation.uniqueElements && variation.uniqueElements.length > 0 && (
                          <div>
                            <p className="text-sm font-medium">固有要素:</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {variation.uniqueElements.map((element, j) => (
                                <Badge key={j} variant="secondary" className="text-xs">
                                  {element}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {variation.difficulty && (
                          <p className="text-sm"><strong>難易度変化:</strong> {variation.difficulty}</p>
                        )}
                        {variation.reward && (
                          <p className="text-sm"><strong>報酬変化:</strong> {variation.reward}</p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">バリエーションが設定されていません</p>
              )}
              
              {result.scenario?.twists && result.scenario.twists.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">どんでん返し</h3>
                  <div className="space-y-2">
                    {result.scenario.twists.map((twist, i) => (
                      <Card key={i}>
                        <CardContent className="pt-4 space-y-1">
                          <p className="text-sm"><strong>タイミング:</strong> {twist.timing}</p>
                          <p className="text-sm"><strong>明かされる内容:</strong> {twist.revelation}</p>
                          <p className="text-sm"><strong>影響:</strong> {twist.impact}</p>
                          {twist.foreshadowing && twist.foreshadowing.length > 0 && (
                            <div>
                              <p className="text-sm font-medium">伏線:</p>
                              <ul className="list-disc list-inside text-xs text-muted-foreground">
                                {twist.foreshadowing.map((hint, j) => (
                                  <li key={j}>{hint}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="participants" className="space-y-4">
              {result.participants?.mainActors && result.participants.mainActors.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">主要登場人物</h3>
                  <div className="space-y-3">
                    {result.participants.mainActors.map((actor, i) => (
                      <Card key={i}>
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-medium">{actor.name}</h4>
                            <Badge variant="outline">{actor.importance}</Badge>
                          </div>
                          <div className="space-y-1 text-sm">
                            <p><strong>役割:</strong> {actor.role}</p>
                            <p><strong>動機:</strong> {actor.motivation}</p>
                            {actor.actions && actor.actions.length > 0 && (
                              <div>
                                <strong>行動:</strong>
                                <ul className="list-disc list-inside text-xs mt-1">
                                  {actor.actions.map((action, j) => (
                                    <li key={j}>{action}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {actor.dialogue && actor.dialogue.length > 0 && (
                              <div>
                                <strong>セリフ:</strong>
                                {actor.dialogue.map((line, j) => (
                                  <p key={j} className="text-xs italic mt-1">「{line}」</p>
                                ))}
                              </div>
                            )}
                            {actor.relationship && (
                              <p className="text-xs text-muted-foreground">関係: {actor.relationship}</p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
              
              {result.participants?.requiredParticipants && (
                <div>
                  <h3 className="font-semibold mb-2">必須参加者</h3>
                  <Card>
                    <CardContent className="pt-4 space-y-1 text-sm">
                      <p><strong>タイプ:</strong> {result.participants.requiredParticipants.type}</p>
                      <p><strong>最小人数:</strong> {result.participants.requiredParticipants.minimumNumber}人</p>
                      {result.participants.requiredParticipants.idealNumber && (
                        <p><strong>理想人数:</strong> {result.participants.requiredParticipants.idealNumber}人</p>
                      )}
                      {result.participants.requiredParticipants.roles && (
                        <div>
                          <strong>役割:</strong>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {result.participants.requiredParticipants.roles.map((role, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">{role}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="choices" className="space-y-4">
              {result.choices?.decisionPoints && result.choices.decisionPoints.length > 0 ? (
                <div className="space-y-4">
                  {result.choices.decisionPoints.map((dp, i) => (
                    <Card key={i}>
                      <CardHeader>
                        <CardTitle className="text-base">決定ポイント {i + 1}: {dp.timing}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <p className="text-sm mb-1">{dp.situation}</p>
                          <div className="flex gap-2">
                            <Badge variant={dp.reversible ? 'secondary' : 'destructive'}>
                              {dp.reversible ? '取り消し可能' : '取り消し不可'}
                            </Badge>
                            <Badge>{dp.urgency}</Badge>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium">選択肢</h4>
                          {dp.options.map((opt, j) => (
                            <Card key={j}>
                              <CardContent className="pt-3">
                                <h5 className="font-medium text-sm mb-1">{opt.label}</h5>
                                <p className="text-xs mb-2">{opt.description}</p>
                                
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  {opt.advantages && opt.advantages.length > 0 && (
                                    <div>
                                      <strong className="text-green-600">メリット:</strong>
                                      <ul className="list-disc list-inside">
                                        {opt.advantages.map((adv, k) => (
                                          <li key={k}>{adv}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  
                                  {opt.disadvantages && opt.disadvantages.length > 0 && (
                                    <div>
                                      <strong className="text-red-600">デメリット:</strong>
                                      <ul className="list-disc list-inside">
                                        {opt.disadvantages.map((dis, k) => (
                                          <li key={k}>{dis}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                                
                                {opt.requirement && (
                                  <p className="text-xs text-muted-foreground mt-2">
                                    <strong>必要条件:</strong> {opt.requirement}
                                  </p>
                                )}
                                
                                {opt.skillCheck && (
                                  <p className="text-xs text-muted-foreground">
                                    <strong>判定:</strong> {opt.skillCheck}
                                  </p>
                                )}
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                        
                        {dp.defaultOption && (
                          <div>
                            <h4 className="text-sm font-medium">デフォルト（選ばない場合）</h4>
                            <Card>
                              <CardContent className="pt-3">
                                <p className="text-xs">{dp.defaultOption.description}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  <strong>結果:</strong> {dp.defaultOption.consequence}
                                </p>
                              </CardContent>
                            </Card>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">選択肢が設定されていません</p>
              )}
            </TabsContent>
            
            <TabsContent value="consequences" className="space-y-4">
              {result.consequences?.immediate && (
                <div>
                  <h3 className="font-semibold mb-2">即座の結果</h3>
                  <Card>
                    <CardContent className="pt-4 space-y-2 text-sm">
                      <p>{result.consequences.immediate.description}</p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <p><strong>明白さ:</strong> {result.consequences.immediate.visibility}</p>
                        <p><strong>持続時間:</strong> {result.consequences.immediate.duration}</p>
                        <p><strong>取消可能性:</strong> {result.consequences.immediate.reversibility}</p>
                      </div>
                      {result.consequences.immediate.affectedParties && (
                        <div>
                          <strong>影響を受ける対象:</strong>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {result.consequences.immediate.affectedParties.map((party, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{party}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
              
              {result.consequences?.rewards && (
                <div>
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Gift className="w-4 h-4" />
                    報酬
                  </h3>
                  <Card>
                    <CardContent className="pt-4 space-y-2 text-sm">
                      <p><strong>経験値:</strong> {result.consequences.rewards.experience || 'なし'}</p>
                      
                      {result.consequences.rewards.items && result.consequences.rewards.items.length > 0 && (
                        <div>
                          <strong>アイテム:</strong>
                          <ul className="list-disc list-inside text-xs mt-1">
                            {result.consequences.rewards.items.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {result.consequences.rewards.knowledge && result.consequences.rewards.knowledge.length > 0 && (
                        <div>
                          <strong>知識:</strong>
                          <ul className="list-disc list-inside text-xs mt-1">
                            {result.consequences.rewards.knowledge.map((know, i) => (
                              <li key={i}>{know}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {result.consequences.rewards.reputation && result.consequences.rewards.reputation.length > 0 && (
                        <div>
                          <strong>評判:</strong>
                          {result.consequences.rewards.reputation.map((rep, i) => (
                            <div key={i} className="text-xs mt-1">
                              {rep.faction}: {rep.change} ({rep.reason})
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="progression" className="space-y-4">
              {result.progression?.phases && result.progression.phases.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">フェーズ</h3>
                  <div className="space-y-3">
                    {result.progression.phases.map((phase, i) => (
                      <Card key={i}>
                        <CardHeader>
                          <CardTitle className="text-base">
                            フェーズ {phase.number}: {phase.name}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm">
                          <p>{phase.description}</p>
                          
                          {phase.objectives && phase.objectives.length > 0 && (
                            <div>
                              <strong>目標:</strong>
                              <ul className="list-disc list-inside text-xs mt-1">
                                {phase.objectives.map((obj, j) => (
                                  <li key={j}>{obj}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          <p><strong>完了条件:</strong> {phase.completion}</p>
                          
                          {phase.timeLimit && (
                            <p><strong>時間制限:</strong> {phase.timeLimit}</p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
              
              {result.progression?.pacing && (
                <div>
                  <h3 className="font-semibold mb-2">ペーシング</h3>
                  <Card>
                    <CardContent className="pt-4 space-y-2 text-sm">
                      <p><strong>推奨時間:</strong> {result.progression.pacing.suggestedDuration}</p>
                      
                      {result.progression.pacing.intensity && result.progression.pacing.intensity.length > 0 && (
                        <div>
                          <strong>強度:</strong>
                          {result.progression.pacing.intensity.map((int, i) => (
                            <div key={i} className="text-xs mt-1">
                              フェーズ{int.phase}: {int.level} - {int.description}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="mechanics" className="space-y-4">
              {result.mechanics?.challenges && result.mechanics.challenges.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">判定・チャレンジ</h3>
                  <div className="space-y-2">
                    {result.mechanics.challenges.map((challenge, i) => (
                      <Card key={i}>
                        <CardContent className="pt-4 space-y-2 text-sm">
                          <div className="flex justify-between items-start">
                            <h4 className="font-medium">{challenge.type}</h4>
                            <Badge>DC {challenge.difficulty}</Badge>
                          </div>
                          <p className="text-xs">{challenge.description}</p>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              {challenge.criticalSuccess && (
                                <p><strong className="text-purple-600">大成功:</strong> {challenge.criticalSuccess}</p>
                              )}
                              <p><strong className="text-green-600">成功:</strong> {challenge.success}</p>
                            </div>
                            <div>
                              <p><strong className="text-orange-600">失敗:</strong> {challenge.failure}</p>
                              {challenge.criticalFailure && (
                                <p><strong className="text-red-600">大失敗:</strong> {challenge.criticalFailure}</p>
                              )}
                            </div>
                          </div>
                          {challenge.alternatives && challenge.alternatives.length > 0 && (
                            <div>
                              <strong>代替手段:</strong>
                              <ul className="list-disc list-inside text-xs mt-1">
                                {challenge.alternatives.map((alt, j) => (
                                  <li key={j}>{alt}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
              
              {result.mechanics?.resources && (
                <div>
                  <h3 className="font-semibold mb-2">リソース</h3>
                  <div className="space-y-2">
                    {result.mechanics.resources.required && result.mechanics.resources.required.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-sm">必要リソース</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-1">
                            {result.mechanics.resources.required.map((res, i) => (
                              <div key={i} className="text-xs">
                                <strong>{res.type}:</strong> {res.amount} ({res.purpose})
                                {res.alternatives && res.alternatives.length > 0 && (
                                  <span className="text-muted-foreground"> または {res.alternatives.join(', ')}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}